import { DATASET_ID_PATH, DATASET_VERSION_ID_PATH, BASE_EMBED_MODEL, TRAIN_TEMPLATE_PATH } from 'modules/Models/components/ModelForm/utils/utils';

// Update this to the default model you want to use
export const DEFAULT_MODEL = 'lmdeploy-llama3_2-1b-instruct-new';

export type FieldValueMapType<T = unknown> = { [key: string]: T };

export type MessageType = 'user' | 'model';

export type ChatHistoryType = {
  type: MessageType;
  message: string;
};

export interface FormikInitialValues {
  model?: CF.API.Models.Model;
  modelVersionId?: string;
  fieldValueMap: FieldValueMapType;
  modelParameters: CF.API.Models.ModelTypeFieldsEntity[];
  trainingTemplateFields: CF.API.Models.ModelTypeEnumOptionsEntity[];
  chatHistory: ChatHistoryType[];
  modelInput: string;
  modelOutput: string;
  isModelOutputLoading: boolean;
  errorState: { title: string; description: string } | undefined;
  selectedView: 'ui' | 'api';
}

const getTrainingTemplateFields = (fields: CF.API.Models.ModelTypeFieldsEntity[]): CF.API.Models.ModelTypeEnumOptionsEntity[] => {
  return (fields.find((field) => 'model_type_enum_options' in field)?.model_type_enum_options || []) as CF.API.Models.ModelTypeEnumOptionsEntity[];
};

export const getFieldValueMap = (
  model: CF.API.Models.Model,
  modelTypes: CF.API.Models.ModelTypesEntity[],
): [FieldValueMapType, CF.API.Models.ModelTypeEnumOptionsEntity[], CF.API.Models.ModelTypeFieldsEntity[]] => {
  // Initialize fieldValueMap with either values from model if it exists or the default values
  const getInputValue = (obj: CF.API.Models.ModelVersion, path: string, defaultValue: unknown): unknown => {
    const objPath = path.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result = objPath.reduce((acc, key) => acc && acc[key], obj as any);
    if (typeof result === 'object' && [DATASET_ID_PATH, DATASET_VERSION_ID_PATH, BASE_EMBED_MODEL].includes(path)) {
      result = result.id;
    }
    return result === undefined ? (defaultValue === undefined ? null : defaultValue) : result;
  };

  const modelType = model?.model_type_id || '';
  const modelTypeDetails = modelTypes.find((modelTypeObject) => modelTypeObject.id === modelType);

  const legacyParamSpecs =
    modelTypeDetails?.model_type_fields?.filter((each) => {
      return each.path.startsWith('output_info.params.');
    }) ?? [];

  // eval_info should only be used for evaluating model. Not for creating or editing.
  const modelTypeFields = modelTypeDetails?.model_type_fields?.filter((field) => !field.path.includes('eval_info')) || [];
  const trainingTemplateFields = getTrainingTemplateFields(modelTypeFields);

  const fieldValueMap: FieldValueMapType = modelTypeFields.reduce(
    (acc, field) => {
      const fieldValue = model?.id ? getInputValue(model.model_version, field.path, field.default_value) : field.default_value;
      if (field.path === TRAIN_TEMPLATE_PATH) {
        // Initialize training template settings with version config values
        const modelTypeEnumOption = trainingTemplateFields.find((options) => options.id === (fieldValue as string))?.model_type_fields || [];
        modelTypeEnumOption.forEach((param) => {
          const { path: optionPath, default_value } = param;
          const templateFieldValue = model ? getInputValue(model.model_version, optionPath, default_value) : default_value;
          acc[optionPath] = templateFieldValue;
        });
      }
      acc[field.path] = fieldValue;
      return acc;
    },
    { modelId: model.id } as FieldValueMapType,
  );

  return [fieldValueMap, trainingTemplateFields, legacyParamSpecs];
};
